import { Injectable } from '@angular/core';
import { EVENT_CLICK_TYPE } from '../../../../constants/utils';
import { DashboardResponse } from '../../../../models/login';
import { AnalyticsParams, ClickEventAnalytics } from '../../../../models/utils';
import { FilterData } from '../../../../models/utils/analytics-extended.interfaces';
import { SearchAnalyticsData } from '../../../../types/analytics.interface';
import { AnalyticsDataPopulateSuperHelperService } from './analytics-data-populate-super-helper.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsDataPopulateService extends AnalyticsDataPopulateSuperHelperService {
  constructor() {
    super();
    this.initializeUserSession();
  }

  private initializeUserSession(): void {
    this.userSession
      .getUserSession()
      .subscribe((userStatus: DashboardResponse) => {
        this.userStatusData = userStatus;
      });
  }

  private handleInputElementType({
    elementRef,
    eventType,
    action,
    pageSection
  }: Omit<AnalyticsParams, 'tagName'>): ClickEventAnalytics | null {
    const type = (elementRef as HTMLInputElement).type as EVENT_CLICK_TYPE;

    const creators: Partial<
      Record<EVENT_CLICK_TYPE, typeof this.createCheckboxAnalytics>
    > = {
      [EVENT_CLICK_TYPE.CHECKBOX]: this.createCheckboxAnalytics,
      [EVENT_CLICK_TYPE.TEXT]: this.createTextAnalytics
    };

    const fn = creators[type];
    return fn
      ? fn.call(this, elementRef, eventType, action, pageSection)
      : null;
  }

  private processButtonAndAnchorElements(
    params: AnalyticsParams,
    pageAttribute?: string,
    componentType?: string,
    componentName?: string,
    useDataAttributes?: boolean
  ): ClickEventAnalytics | null {
    switch (params.tagName) {
      case EVENT_CLICK_TYPE.BUTTON:
        return this.createButtonAnalytics(
          params,
          pageAttribute,
          componentType,
          componentName
        );
      case EVENT_CLICK_TYPE.ANCHOR:
        return this.createLinkAnalytics(
          params,
          pageAttribute,
          componentType,
          componentName,
          useDataAttributes
        );
      case EVENT_CLICK_TYPE.INPUT:
        return this.handleInputElementType(params);
      default:
        return null;
    }
  }

  private processInputAndDivElements(
    params: AnalyticsParams
  ): ClickEventAnalytics | null {
    switch (params.tagName) {
      case EVENT_CLICK_TYPE.INPUT:
        return this.handleInputElementType(params);
      case EVENT_CLICK_TYPE.DIV: {
        const label = this.getElementLabel(params.elementRef);
        if (label === 'advanced search') {
          return this.createLinkAnalytics(params);
        }
        return null;
      }
      default:
        return null;
    }
  }

  private processElementByType(
    params: AnalyticsParams,
    pageAttribute?: string,
    componentType?: string,
    componentName?: string,
    useDataAttributes?: boolean
  ): ClickEventAnalytics | null {
    return (
      this.processButtonAndAnchorElements(
        params,
        pageAttribute,
        componentType,
        componentName,
        useDataAttributes
      ) || this.processInputAndDivElements(params)
    );
  }

  populateAnalyticsData(
    el: HTMLElement,
    event?: Event,
    search?: SearchAnalyticsData,
    useAttrs?: boolean,
    filters?: FilterData[]
  ): void {
    if (!this.userStatusData) return;

    const params: AnalyticsParams = {
      elementRef: el,
      eventType: this.getEventType(event),
      action: this.getDataAttribute(el, 'data-acdl-action'),
      pageSection: this.getDataAttribute(el, 'data-acdl-page-section'),
      tagName: el.tagName.toLowerCase()
    };

    const componentType = this.getDataAttribute(el, 'data-acdl-component-type');
    const componentName = this.getDataAttribute(el, 'data-acdl-component-name');
    const pageAttribute = this.getDataAttribute(el, 'data-acdl-page');

    const data = this.processElementByType(
      params,
      pageAttribute,
      componentType,
      componentName,
      useAttrs
    );
    if (!data) return;

    this.analyticsService.setAnalytics(
      this.enrichAnalyticsData(data, filters, search)
    );
  }
}
